import { FC, MouseEvent } from 'react'
import { Select, SelectOption } from '@microservices/wiskey-react-components'
import ClearIcon from '@mui/icons-material/Clear'
import { Box, IconButton, SelectChangeEvent } from '@mui/material'
import { Cell } from '@tanstack/react-table'

import { PINNED_COLUMN } from '@constants'
import { FormattedColumnRestrictions, RESTRICTION_TYPE, RestrictionDTO } from '@types'

interface PinningCellProps {
  cell: Cell<FormattedColumnRestrictions, unknown>
  updateRestrictions: (restrictions: RestrictionDTO[]) => Promise<unknown>
  viewId: number
}

const PINNING_OPTIONS: SelectOption[] = [
  { id: PINNED_COLUMN.LEFT, name: 'left' },
  { id: PINNED_COLUMN.RIGHT, name: 'right' },
]

export const PinningCell: FC<PinningCellProps> = ({ cell, updateRestrictions, viewId }) => {
  const columnRestrictions = cell.row.original
  const restriction = columnRestrictions.fix

  const value = restriction?.value || ''

  const handleUpdateRestriction = async (
    event: SelectChangeEvent<string | number> | MouseEvent
  ) => {
    const newValue = (event?.target as SelectChangeEvent['target'])?.value
    const isPinning = newValue === PINNED_COLUMN.LEFT || newValue === PINNED_COLUMN.RIGHT

    await updateRestrictions([
      {
        viewId,
        fieldCode: columnRestrictions.fieldCode,
        fieldName: columnRestrictions.fieldName,
        restrictionType: RESTRICTION_TYPE.FIX,
        value: newValue,
      },
    ])

    if (isPinning && columnRestrictions.show?.value !== true) {
      await updateRestrictions([
        {
          viewId,
          fieldCode: columnRestrictions.fieldCode,
          fieldName: columnRestrictions.fieldName,
          restrictionType: RESTRICTION_TYPE.SHOW,
          value: true,
        },
      ])
    }
  }

  return (
    <Box
      sx={theme => ({
        position: 'relative',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        '& .MuiInputBase-root': {
          color: theme.palette.color.field,
          border: theme.palette.border.field,
          paddingLeft: '6px',
        },
        '& .MuiSelect-select': {
          padding: '0px 4px !important',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none !important',
        },
      })}
    >
      <Select
        displayEmpty
        fullWidth
        options={PINNING_OPTIONS}
        placeholder='Select pinning'
        size='small'
        value={value}
        onChange={handleUpdateRestriction}
      />
      {value && (
        <IconButton
          size='small'
          sx={{
            position: 'absolute',
            right: '28px',
            padding: '4px',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
          onClick={handleUpdateRestriction}
        >
          <ClearIcon fontSize='small' />
        </IconButton>
      )}
    </Box>
  )
}
